/**
 * Utility helpers for the CandlestickChart toolbar "AI Signals" feature.
 *
 * Extracted so the logic can be unit-tested without rendering the full chart.
 */

/**
 * Return the emoji icon for a given signal string.
 *   up / bull → ⬆️
 *   down / bear → ⬇️
 *   anything else → ➡️
 */
export function trendIcon(signal) {
  if (!signal) return '➡️';
  const s = String(signal).toLowerCase();
  if (s.includes('up') || s.includes('bull')) return '⬆️';
  if (s.includes('down') || s.includes('bear')) return '⬇️';
  return '➡️';
}

/**
 * Return the hex colour for a given signal string.
 *   up / bull → green (#16a34a)
 *   down / bear → red (#dc2626)
 *   anything else → neutral (depends on isDark)
 */
export function trendColor(signal, isDark = false) {
  if (!signal) return isDark ? '#d1d5db' : '#111827';
  const s = String(signal).toLowerCase();
  if (s.includes('up') || s.includes('bull')) return '#16a34a';
  if (s.includes('down') || s.includes('bear')) return '#dc2626';
  return isDark ? '#d1d5db' : '#111827';
}

/**
 * Compute the toolbar trend object consumed by CandlestickChart's JSX.
 *
 * @param {object|null} stockInfo       – stock detail (from API)
 * @param {object|null} predictions     – prediction payload (optional, for date fallback)
 * @param {boolean}     isDark          – dark-mode flag
 * @returns {object|null} toolbarTrend – { dateLabel, model1, model2, modelEnsemble } or null
 */
export function computeToolbarTrend(stockInfo, predictions, isDark) {
  try {
    const cs = stockInfo?.model_stats?.current_signals;
    const m1 = cs?.model1_signal;
    const m2 = cs?.model2_signal;
    const me = cs?.model_ensemble_signal;
    if (!m1 && !m2 && !me) return null;

    // Derive date label
    let dateLabel = '';
    try {
      let dateSource = cs?.date || stockInfo?.last_updated;
      if (
        !dateSource &&
        predictions &&
        Array.isArray(predictions.docs) &&
        predictions.docs.length
      ) {
        const latestDoc = [...predictions.docs].sort(
          (a, b) =>
            new Date(b.date || b.created_at) - new Date(a.date || a.created_at),
        )[0];
        dateSource = latestDoc.created_at || latestDoc.date;
      }
      if (dateSource) {
        let dv = dateSource;
        if (typeof dv === 'object' && dv.$date) dv = dv.$date;
        const d = new Date(dv);
        if (!isNaN(d.getTime())) {
          dateLabel = new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: '2-digit',
            timeZone: 'Europe/Berlin',
          }).format(d);
        }
      }
    } catch {}

    return {
      dateLabel,
      model1: m1
        ? { signal: String(m1), icon: trendIcon(m1), color: trendColor(m1, isDark) }
        : null,
      model2: m2
        ? { signal: String(m2), icon: trendIcon(m2), color: trendColor(m2, isDark) }
        : null,
      modelEnsemble: me
        ? { signal: String(me), icon: trendIcon(me), color: trendColor(me, isDark) }
        : null,
    };
  } catch {
    return null;
  }
}
